import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { PolotnoStoreKeys, usePolotnoState } from '@/pages/AdvancedEditor/hooks/polotno-store';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { IProjectTemplate } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress, IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { CropModeToAspectRatio } from '../../../header/CropDropdown';
import { fetchTemplateRawData, useApplyTemplate } from '../hooks';
import { SceneTile } from './SceneTile';

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${theme.colors.black[700]};
`;

const StyledBackButton = styled(IconButton)`
    padding: 0;
    border-radius: 12px;
    height: 40px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const TemplateDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
`;

const BackButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const BackText = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${theme.colors.black[900]};
`;

const TransparentButton = styled(Button)`
    background-color: transparent;
    border: 1px solid ${theme.colors.black[200]};
    height: 44px;
    padding: 10px 16px;

    font-size: 16px;
    font-weight: 700;
    color: ${theme.colors.black[900]};

    &.MuiButtonBase-root:hover {
        background-color: #f3f3f3 !important;
    }

    &.MuiButtonBase-root:disabled {
        background-color: transparent !important;
    }
`;

const TemplatesGridContainer = styled.div`
    height: calc(100% - 40px - 44px - 32px);
    overflow: auto;
    scrollbar-gutter: stable;
`;

const TemplatesGrid = styled.div<{ hidden: boolean }>`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    opacity: ${props => (props.hidden ? 0 : 1)};
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

interface Props {
    selectedTemplate: IProjectTemplate;
    selectedTemplateIndex: number;
    onBack: () => void;
    search: string;
    filter: string;
}

export const TemplateDetails = observer(({ selectedTemplate, selectedTemplateIndex, onBack, search, filter }: Props) => {
    const mixpanel = useAnalytics();
    const { store, scenesPageActionsFreeze } = useAdvancedEditorContext();

    const [cropSettings] = usePolotnoState(PolotnoStoreKeys.cropSettings);
    const [aspectRatio] = usePolotnoState(PolotnoStoreKeys.aspectRatio);

    const { applyTemplate, applyScene } = useApplyTemplate();
    const [isImagesLoaded, setIsImagesLoaded] = useState(false);

    const reportTemplateClick = (action: 'add-all-scenes' | 'add-selected-scene') => {
        mixpanel?.track('templates-preview', {
            event: 'click',
            componentType: 'side-panel',
            action,
            templateName: selectedTemplate.name,
            templateId: selectedTemplate.id,
            aspectRatio: aspectRatio || CropModeToAspectRatio[cropSettings],
            useCaseFilter: [filter],
            textSearch: search,
            index: selectedTemplateIndex,
        });
    };

    const handleApplySingleScene = async (index: number) => {
        const rawStore = await fetchTemplateRawData(selectedTemplate);
        applyScene(rawStore, index, store.activePage?.id);
        reportTemplateClick('add-selected-scene');
    };

    const handleApplyAllScenes = async () => {
        const rawStore = await fetchTemplateRawData(selectedTemplate);

        // there is now good way to check if scene is duplicated/added or from templates
        // so we need to freeze the mixpanel report for 1 second after the scene is applied
        scenesPageActionsFreeze.current = true;

        applyTemplate(rawStore);
        reportTemplateClick('add-all-scenes');

        setTimeout(() => {
            scenesPageActionsFreeze.current = false;
        }, 1000);
    };

    useEffect(() => {
        setIsImagesLoaded(false);

        const images = selectedTemplate.scenesThumbnailsUrls;

        if (!images.length) return;

        const imagesPromises = images.map(
            image =>
                new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = image;
                    img.onload = () => resolve(img);

                    setTimeout(() => {
                        reject(new Error('Image failed to load'));
                    }, 2000);
                })
        );

        Promise.all(imagesPromises).finally(() => {
            setIsImagesLoaded(true);
        });
    }, [selectedTemplate.scenesThumbnailsUrls]);

    useEffect(() => {
        mixpanel?.track('template-preview', {
            event: 'view',
            componentType: 'side-panel',
            templateName: selectedTemplate.name,
            templateId: selectedTemplate.id,
            aspectRatio: aspectRatio || CropModeToAspectRatio[cropSettings],
            useCaseFilter: [filter],
            textSearch: search,
            index: selectedTemplateIndex,
        });
    }, [selectedTemplate]);

    return (
        <TemplateDetailsContainer>
            <BackButtonContainer>
                <StyledBackButton onClick={onBack}>
                    <StyledMaskedIcon src={ArrowLeft} size="32px" />
                </StyledBackButton>
                <BackText>{selectedTemplate.name}</BackText>
            </BackButtonContainer>

            <TransparentButton onClick={handleApplyAllScenes} data-testid="apply-all-scenes-button">
                {selectedTemplate.scenesThumbnailsUrls.length
                    ? `Apply ${selectedTemplate.scenesThumbnailsUrls.length} scenes`
                    : 'Apply 1 scene'}
            </TransparentButton>

            {!isImagesLoaded && (
                <LoaderWrapper>
                    <CircularProgress />
                </LoaderWrapper>
            )}

            <TemplatesGridContainer>
                <TemplatesGrid id="scenes-grid" hidden={!isImagesLoaded}>
                    {selectedTemplate.scenesThumbnailsUrls.map((sceneThumbnailUrl, idx) => (
                        <SceneTile key={sceneThumbnailUrl} onClick={() => handleApplySingleScene(idx)} data-testid="scene-tile">
                            <img src={sceneThumbnailUrl} loading="lazy" />
                        </SceneTile>
                    ))}
                </TemplatesGrid>
            </TemplatesGridContainer>
        </TemplateDetailsContainer>
    );
});
